import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Heart, Shield, Leaf, Users, Award, Globe, Clock, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';

const AboutPage = () => {
  const values = [
    {
      icon: Heart,
      title: 'Customer-Centric',
      description: 'Every journey is designed around our clients unique needs and preferences.'
    },
    {
      icon: Shield,
      title: 'Integrity',
      description: 'Transparent pricing and honest recommendations you can trust.'
    },
    {
      icon: Leaf,
      title: 'Sustainability',
      description: 'Promoting eco-friendly travel and supporting local communities.'
    },
    {
      icon: CheckCircle2,
      title: 'Excellence',
      description: 'Delivering high-quality experiences from start to finish.'
    }
  ];

  const reasons = [
    'Experienced travel consultants with global expertise',
    '24/7 customer support during your trip',
    'Strong partnerships with hotels and tour operators',
    'Flexible payment plans and competitive rates',
    'Personalized itineraries tailored to your interests',
    'Commitment to sustainable and responsible tourism'
  ];

  const stats = [
    {
      icon: Users,
      stat: '500+',
      label: 'Happy Travelers'
    },
    {
      icon: Globe,
      stat: '15+',
      label: 'Destinations'
    },
    {
      icon: Award,
      stat: '4.9',
      label: 'Average Rating'
    },
    {
      icon: Clock,
      stat: '4+',
      label: 'Years Experience'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="About Us"
        description="Learn about Peregrine Tours & Safaris, your trusted partner for unforgettable African adventures since 2020."
        keywords="About Peregrine Tours, Safari Company, African Travel Experts, Sustainable Tourism"
        canonicalUrl="https://www.peregrinetoursandsafaris.com/about"
        schema={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "About Peregrine Tours & Safaris",
          "description": "Learn about our mission, values, and story of providing exceptional safari experiences in Africa.",
          "publisher": {
            "@type": "Organization",
            "name": "Peregrine Tours & Safaris"
          }
        }}
      />
      <Navbar />


      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-b from-muted/30 to-background">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto mb-12">
            <span className="inline-block px-4 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full mb-4">
              About Peregrine Tours
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 font-serif leading-tight">
              Your Trusted African Adventure Partner
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Since 2020, we've been creating unforgettable journeys across Southern and Eastern Africa, connecting travelers with the wild beauty and rich culture of the continent.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.pexels.com/photos/59989/elephant-herd-of-elephants-african-bush-elephant-africa-59989.jpeg"
                  alt="Elephant herd in Africa"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent" />
              </div>
            </div>

            <div className="space-y-6">
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground font-serif leading-tight">
                Our Story
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Peregrine Tours & Safaris was born from a deep passion for Africa's natural wonders and cultural heritage. Established in 2020, we've grown from a small team of safari enthusiasts to a premier travel solutions provider.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Our mission is simple yet profound: to inspire and connect people through exceptional travel experiences that celebrate culture, nature, and human connection. We specialize in tailor-made holiday packages, corporate travel management, and adventure expeditions.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Every journey we craft is a testament to our commitment to excellence, sustainability, and creating memories that last a lifetime.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-gradient-to-b from-muted/30 to-background">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6 font-serif">
              Our Core Values
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              These principles guide everything we do, ensuring every safari experience exceeds expectations.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="p-6 border-2 hover:border-primary/30 transition-all duration-300 hover:shadow-lg text-center">
                  <div className="inline-flex w-16 h-16 rounded-full bg-primary/10 items-center justify-center mb-4">
                    <Icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3 font-serif">{value.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {value.description}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6 font-serif">
                Why Choose Peregrine Tours?
              </h2>
            </div>

            <Card className="p-8 border-2">
              <ul className="grid md:grid-cols-2 gap-6">
                {reasons.map((reason, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-muted-foreground">{reason}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-gradient-to-b from-muted/30 to-background">
        <div className="container-custom">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="p-8 text-center border-2 hover:border-primary/30 transition-all duration-300">
                  <div className="inline-flex w-16 h-16 rounded-full bg-primary/10 items-center justify-center mb-4">
                    <Icon className="w-8 h-8 text-primary" />
                  </div>
                  <div className="text-4xl font-bold text-primary mb-2 font-serif">{item.stat}</div>
                  <div className="text-muted-foreground">{item.label}</div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section-padding bg-background text-center">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground font-serif">
              Ready to Explore the Wild?
            </h2>
            <p className="text-lg text-muted-foreground">
              Your dream African safari is just a click away. Explore our official 2026 activities and start building your custom itinerary today.
            </p>
            <Button asChild size="lg" className="bg-primary hover:bg-primary-dark text-white px-8 h-14 rounded-full group">
              <a href="mailto:reservations@peregrinetoursandsafaris.com?subject=Custom Itinerary Inquiry">
                Start Booking Now
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;